import {
  ConsumableLine,
  MaterialsCalculation,
  ProjectState,
  SurfaceId,
  SurfaceSummary,
  TileCategory,
  TileData,
  TileLine,
} from '../types';
import { calculateSurfaces, SURFACE_LABELS } from './layoutEngine';

export const MATERIAL_DEFAULTS = {
  tileThickness: 9,
  glueBagSize: 25,
  groutPackSize: 5,
  gluePricePerKg: 42,
  groutPricePerKg: 190,
  reservePercent: 10,
} as const;

export const CATEGORY_LABELS: Record<TileCategory, string> = {
  floor: 'Половая плитка',
  darkWall: 'Стеновая тёмная',
  lightWall: 'Стеновая светлая',
  decor: 'Декор',
  border: 'Бордюр / фриз',
};

/** Порядок категорий в отчёте — пол, стены, декор, бордюр */
const CATEGORY_ORDER: TileCategory[] = ['floor', 'lightWall', 'darkWall', 'decor', 'border'];

/**
 * Базовый расход клея на м² в зависимости от формата плитки и зубчатого шпателя.
 * Значения — практика производителей (Ceresit/Litokol) для ровного основания:
 * чем крупнее формат, тем глубже слой и больше расход.
 */
export const glueConsumptionPerM2 = (
  tileWidth: number,
  tileHeight: number,
  coverage: 'thin' | 'standard' | 'large' = 'standard',
): number => {
  const longest = Math.max(tileWidth, tileHeight);
  const area = (tileWidth * tileHeight) / 1_000_000;

  let perM2 = 3.5;
  if (longest <= 100) perM2 = 2.5;
  else if (longest <= 300) perM2 = 3.5;
  else if (longest <= 600) perM2 = 4.5;
  else if (longest <= 900) perM2 = 6;
  else perM2 = 7.5;

  if (area >= 0.9) perM2 += 1;
  if (coverage === 'large') perM2 += 1.5;
  if (coverage === 'thin') perM2 -= 0.5;

  return Math.round(Math.max(1.5, perM2) * 100) / 100;
};

/**
 * Расход затирки по формуле производителей:
 *   кг/м² = ((A + B) / (A × B)) × C × D × 1.6,
 * где A, B — стороны плитки (мм), C — ширина шва (мм),
 * D — глубина шва (≈ толщина плитки, мм), 1.6 — плотность затирки (кг/дм³).
 */
export const groutConsumptionPerM2 = (
  tileWidth: number,
  tileHeight: number,
  jointWidth: number,
  thickness: number,
): number => {
  if (jointWidth <= 0) return 0;
  const a = Math.max(10, tileWidth);
  const b = Math.max(10, tileHeight);
  const depth = Math.max(2, thickness);
  const perM2 = ((a + b) / (a * b)) * jointWidth * depth * 1.6;
  return Math.round(perM2 * 1000) / 1000;
};

const formatTileSize = (tile: TileData): string =>
  `${Math.round(tile.tileWidth)} × ${Math.round(tile.tileHeight)} мм`;

const tileAreaM2 = (tile: TileData | null | undefined): number => {
  const width = Math.max(1, tile?.tileWidth ?? 300);
  const height = Math.max(1, tile?.tileHeight ?? 300);
  return (width * height) / 1_000_000;
};

const roundTo = (value: number, digits: number): number => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const glueTrowelNote = (tile: TileData): string => formatTileSize(tile);

/**
 * Единый источник количества плиток: площадь материала категории на поверхности
 * (мм²) → штук с учётом запаса на подрезку, производственного запаса и упаковки.
 */
const orderedPieces = (
  project: ProjectState,
  category: TileCategory,
  coveredAreaMm2: number,
  wastePercent: number,
  reservePercent: number,
): { pieces: number; packs: number; area: number; areaWithoutPacks: number } => {
  const tile = project.tiles[category];
  const perM2 = tileAreaM2(tile);
  const areaWithoutPacks = coveredAreaMm2 / 1_000_000 * (1 + wastePercent / 100) * (1 + reservePercent / 100);
  const needed = Math.max(0, Math.ceil(areaWithoutPacks / perM2 - 1e-9));
  const perPack = Math.max(0, Math.floor(project.materials?.tilesPerPack?.[category] ?? 0));
  if (perPack > 0) {
    const packs = Math.ceil(needed / perPack);
    return { pieces: packs * perPack, packs, area: packs * perPack * perM2, areaWithoutPacks };
  }
  return { pieces: needed, packs: 0, area: areaWithoutPacks, areaWithoutPacks };
};

export const calculateMaterials = (project: ProjectState): MaterialsCalculation => {
  const settings = project.materials ?? {
    tileThickness: MATERIAL_DEFAULTS.tileThickness,
    glueBagSize: MATERIAL_DEFAULTS.glueBagSize,
    groutPackSize: MATERIAL_DEFAULTS.groutPackSize,
    gluePricePerKg: MATERIAL_DEFAULTS.gluePricePerKg,
    groutPricePerKg: MATERIAL_DEFAULTS.groutPricePerKg,
    reservePercent: MATERIAL_DEFAULTS.reservePercent,
  };
  const wastePercent = project.wastePercent;
  const reservePercent = Math.max(0, settings.reservePercent ?? 0);
  const surfaces = calculateSurfaces(project);

  // (поверхность, категория) → штук: одна и та же карта идёт в отчёт по поверхностям и в спецификацию,
  // поэтому суммы этих двух таблиц всегда сходятся.
  const piecesByCell = new Map<string, { pieces: number; packs: number }>();
  const coveredByCategory = new Map<TileCategory, number>();
  const surfacesByCategory = new Map<TileCategory, string[]>();

  surfaces.forEach(({ surfaceId, calculation }) => {
    const label = SURFACE_LABELS[surfaceId];
    const coveredByCategoryOnSurface = new Map<TileCategory, number>();
    calculation.tiles.forEach((placement) => {
      coveredByCategoryOnSurface.set(
        placement.category,
        (coveredByCategoryOnSurface.get(placement.category) ?? 0) + placement.coveredArea,
      );
    });
    coveredByCategoryOnSurface.forEach((coveredArea, category) => {
      const ordered = orderedPieces(project, category, coveredArea, wastePercent, reservePercent);
      piecesByCell.set(`${surfaceId}|${category}`, { pieces: ordered.pieces, packs: ordered.packs });
      coveredByCategory.set(category, (coveredByCategory.get(category) ?? 0) + coveredArea);
      const labels = surfacesByCategory.get(category) ?? [];
      if (!labels.includes(label)) labels.push(label);
      surfacesByCategory.set(category, labels);
    });
  });

  const summaries: SurfaceSummary[] = surfaces.map(({ surfaceId, calculation, template }) => {
    const total = calculation.tiles.reduce((sum, placement) => {
      const tile = project.tiles[placement.category];
      return sum + placement.coveredArea / 1_000_000 * (1 + wastePercent / 100) * (tile?.price ?? 0);
    }, 0);
    const purchasedTiles = [...piecesByCell.entries()]
      .filter(([key]) => key.startsWith(`${surfaceId}|`))
      .reduce((sum, [, value]) => sum + value.pieces, 0);
    return {
      surfaceId,
      label: SURFACE_LABELS[surfaceId],
      width: calculation.width,
      height: calculation.height,
      area: calculation.area / 1_000_000,
      coveredArea: calculation.coveredArea / 1_000_000,
      wholeTiles: calculation.wholeTiles,
      cutTiles: calculation.cutTiles,
      purchasedTiles,
      templateName: template.name,
      total,
    };
  });

  const coveredArea = summaries.reduce((sum, surface) => sum + surface.coveredArea, 0);
  const areaWithWaste = coveredArea * (1 + wastePercent / 100);
  const areaWithReserve = coveredArea * (1 + wastePercent / 100) * (1 + reservePercent / 100);
  const effectiveReservePercent = roundTo(
    ((1 + wastePercent / 100) * (1 + reservePercent / 100) - 1) * 100,
    2,
  );

  const tiles: TileLine[] = CATEGORY_ORDER
    .filter((category) => coveredByCategory.has(category))
    .map((category) => {
      const tile = project.tiles[category];
      const covered = coveredByCategory.get(category) ?? 0;
      const cells = [...piecesByCell.entries()]
        .filter(([key]) => key.endsWith(`|${category}`))
        .map(([, value]) => value);
      const pieces = cells.reduce((sum, value) => sum + value.pieces, 0);
      const packs = cells.reduce((sum, value) => sum + value.packs, 0);
      // Площадь закупки: при заданных упаковках — по фактическим штукам, иначе по площади с запасами
      const ordered = orderedPieces(project, category, covered, wastePercent, reservePercent);
      const areaWithoutPacks = ordered.areaWithoutPacks;
      const area = packs > 0 ? pieces * tileAreaM2(tile) : areaWithoutPacks;
      return {
        category,
        name: tile?.name || CATEGORY_LABELS[category],
        sku: tile?.sku ?? '',
        format: tile ? formatTileSize(tile) : CATEGORY_LABELS[category],
        area,
        pieces,
        packs,
        pricePerM2: tile?.price ?? 0,
        total: area * (tile?.price ?? 0),
        surfaces: surfacesByCategory.get(category) ?? [],
      };
    })
    .sort((first, second) => second.total - first.total);

  // --- Клей и затирка: расход считается по КАЖДОМУ формату, по его площади облицовки ---
  const glueFormats: Array<{ label: string; perM2: number; covered: number }> = [];
  const groutFormats: Array<{ label: string; perM2: number; covered: number }> = [];
  let glueKgExact = 0;
  let groutKgExact = 0;

  coveredByCategory.forEach((coveredMm2, category) => {
    const tile = project.tiles[category];
    if (!tile || coveredMm2 <= 0) return;
    const covered = coveredMm2 / 1_000_000;
    const gluePer = glueConsumptionPerM2(tile.tileWidth, tile.tileHeight);
    glueKgExact += covered * (1 + reservePercent / 100) * gluePer;
    glueFormats.push({ label: glueTrowelNote(tile), perM2: gluePer, covered });

    const groutPer = groutConsumptionPerM2(
      tile.tileWidth,
      tile.tileHeight,
      project.grout.width,
      settings.tileThickness,
    );
    groutKgExact += covered * groutPer;
    groutFormats.push({ label: glueTrowelNote(tile), perM2: groutPer, covered });
  });

  const formulaBreakdown = (formats: Array<{ label: string; perM2: number; covered: number }>) => {
    // Одинаковые форматы (разные категории одной плитки) схлопываем в одну строку
    const unique = new Map<string, { label: string; perM2: number; covered: number; count: number }>();
    formats.forEach((item) => {
      const key = `${item.label}|${item.perM2}`;
      const existing = unique.get(key);
      if (existing) {
        existing.covered += item.covered;
        existing.count += 1;
        return;
      }
      unique.set(key, { ...item, count: 1 });
    });
    const sorted = [...unique.values()].sort((first, second) => second.covered - first.covered);
    const shown = sorted.slice(0, 3).map((item) => `${item.label} — ${item.perM2} кг/м²`);
    if (sorted.length > shown.length) shown.push(`ещё ${sorted.length - shown.length} формат(а)`);
    return shown.join('; ');
  };

  // Площадь под клей — по факту облицовки (без запаса на подрезку) + производственный запас на замес
  const glueArea = coveredArea * (1 + reservePercent / 100);
  const glueKg = Math.ceil(glueKgExact * 10) / 10;
  const gluePackSize = Math.max(1, settings.glueBagSize);
  const gluePacks = Math.ceil(glueKg / gluePackSize);
  const gluePerM2 = glueArea > 0 ? roundTo(glueKgExact / glueArea, 2) : 0;

  const groutKg = Math.ceil(groutKgExact * 10) / 10;
  const groutPackSize = Math.max(0.5, settings.groutPackSize);
  const groutPacks = Math.ceil(groutKg / groutPackSize);
  const groutPerM2 = coveredArea > 0 ? roundTo(groutKgExact / coveredArea, 3) : 0;

  const consumables: ConsumableLine[] = [
    {
      kind: 'glue',
      title: `Клей плиточный (мешок ${gluePackSize} кг)`,
      perM2: gluePerM2,
      kg: glueKg,
      packs: gluePacks,
      packSize: gluePackSize,
      pricePerKg: settings.gluePricePerKg,
      total: gluePacks * gluePackSize * settings.gluePricePerKg,
      note: glueFormats.length
        ? `По форматам: ${formulaBreakdown(glueFormats)} (слой под гребёнку)`
        : 'Расчёт по площади облицовки',
    },
    {
      kind: 'grout',
      title: `Затирка (упаковка ${groutPackSize} кг)`,
      perM2: groutPerM2,
      kg: groutKg,
      packs: groutPacks,
      packSize: groutPackSize,
      pricePerKg: settings.groutPricePerKg,
      total: groutPacks * groutPackSize * settings.groutPricePerKg,
      note: groutFormats.length
        ? `Шов ${project.grout.width} мм, глубина ${settings.tileThickness} мм · по форматам: ${formulaBreakdown(groutFormats)}`
        : `Шов ${project.grout.width} мм, глубина ${settings.tileThickness} мм`,
    },
  ];

  const tilesTotal = tiles.reduce((sum, line) => sum + line.total, 0);
  const consumablesTotal = consumables.reduce((sum, line) => sum + line.total, 0);

  return {
    area: coveredArea,
    areaWithWaste,
    areaWithReserve,
    wastePercent,
    reservePercent,
    effectiveReservePercent,
    surfaces: summaries,
    tiles,
    consumables,
    tilesTotal,
    consumablesTotal,
    total: tilesTotal + consumablesTotal,
  };
};

export const surfaceSummaryById = (
  calculation: MaterialsCalculation,
  surfaceId: SurfaceId,
) => calculation.surfaces.find((surface) => surface.surfaceId === surfaceId);
